/**
 * Road Segments Inventory Config
 *
 * @module roadways/config
 * @description Configuración de inventario para segmentos viales
 */

import type { InventoryPanelConfig, InventoryRow } from '../../../layouts/panels/LeftPanel';
import { useRoadSegments } from '../hooks/useRoadSegments';
import { useRoadwaysElementColorsStore } from '../store/element-colors-store';
import type { RoadSegment } from '../types/road-segment.types';

/**
 * Configuración del panel de inventario de segmentos viales
 */
export const roadSegmentsInventoryConfig: InventoryPanelConfig<RoadSegment> = {
  entityType: 'road-segment',
  displayName: 'Segmentos Viales',
  displayNameSingular: 'segmento vial',

  narrowColumns: [
    { key: 'code', label: 'Código', title: 'Código del segmento', width: '70px' },
    { key: 'name', label: 'Nombre', title: 'Nombre del segmento', width: '1fr' },
  ],

  wideColumns: [
    { key: 'code', label: 'Cód', title: 'Código del segmento', width: '70px' },
    { key: 'name', label: 'Nombre', title: 'Nombre del segmento', width: '1.5fr' },
    { key: 'subtype', label: 'Tipo', title: 'Clasificación funcional', width: '100px' },
    { key: 'surface', label: 'Superficie', title: 'Tipo de superficie', width: '90px' },
    { key: 'condition', label: 'Condición', title: 'Estado de la vía', width: '80px' },
    { key: 'lanes', label: 'Carriles', title: 'Número de carriles', width: '70px' },
  ],

  useData: (projectId: string) => {
    const {
      roadSegments,
      isLoading,
      error,
      loadRoadSegments,
      deleteRoadSegment,
    } = useRoadSegments({ projectId, autoLoad: true });

    return {
      data: roadSegments,
      isLoading,
      error,
      reload: loadRoadSegments,
      delete: deleteRoadSegment,
    };
  },

  buildRow: (segment: RoadSegment): InventoryRow => ({
    id: segment.id,
    code: segment.code,
    name: segment.name,
    subtype: segment.subtype,
    surface: segment.surface,
    condition: segment.condition,
    lanes: segment.lanes?.toString() || '-',
  }),

  events: {
    select: 'roadways-segment-select',
    visibilityToggle: 'roadways-segment-visibility-toggle',
    hover: 'roadways-segment-hover',
    zoom: 'roadways-segment-zoom',
    created: 'road-segment-created',
    updated: 'road-segment-updated',
    deleted: 'road-segment-deleted',
  },

  getZoomTarget: (segment) => ({
    type: 'line',
    coordinates: segment.geometry.coordinates,
  }),

  colorStore: {
    getColor: (key) => useRoadwaysElementColorsStore.getState().getColor(key),
    setColor: (key, hex) => useRoadwaysElementColorsStore.getState().setColor(key, hex),
    colorKey: 'road-segments',
  },

  moduleId: 'roadways',
};
